import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { API } from '../utils/api';
import LucideIcon from './LucideIcon';

interface Level {
  questId: string;
  levelNumber: number;
  title: string;
  story: string;
  objective: string;
  difficulty: string;
  hints: string[];
  points: number;
}

interface RubricBreakdown {
  constraintAdherence: number;
  constraintReasoning: string;
  scenarioRelevance: number;
  scenarioReasoning: string;
  objectiveAlignment: number;
  objectiveReasoning: string;
  techniqueApplication: number;
  techniqueReasoning: string;
}

const RUBRIC_ROWS: { key: keyof RubricBreakdown; reasoningKey: keyof RubricBreakdown; label: string }[] = [
  { key: 'constraintAdherence', reasoningKey: 'constraintReasoning', label: 'Constraint adherence' },
  { key: 'scenarioRelevance', reasoningKey: 'scenarioReasoning', label: 'Scenario relevance' },
  { key: 'objectiveAlignment', reasoningKey: 'objectiveReasoning', label: 'Objective alignment' },
  { key: 'techniqueApplication', reasoningKey: 'techniqueReasoning', label: 'Technique application' },
];

const panel: React.CSSProperties = {
  background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 14, padding: 22,
};

const QuestLevel: React.FC = () => {
  const { questId } = useParams<{ questId: string }>();
  const navigate = useNavigate();
  const [currentLevel, setCurrentLevel] = useState<number | null>(null);
  const [level, setLevel] = useState<Level | null>(null);
  const [userPrompt, setUserPrompt] = useState('');
  const [aiResponse, setAiResponse] = useState('');
  const [validation, setValidation] = useState<{ passed: boolean; feedback: string; breakdown?: RubricBreakdown | null } | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const loadQuestProgress = useCallback(async () => {
    setLoading(true);
    try {
      const response = await API.get(`/quests/${questId}/progress`);
      setCurrentLevel(response.currentLevel || 1);
    } catch (error) {
      setCurrentLevel(1);
    } finally {
      setLoading(false);
    }
  }, [questId]);

  const fetchLevel = useCallback(async (levelNum: number) => {
    setLoading(true);
    setError('');
    try {
      const response = await API.get(`/quests/${questId}/level/${levelNum}`);
      setLevel(response.level);
      setAiResponse('');
      setValidation(null);
      setUserPrompt('');
    } catch (e: any) {
      setError(e?.message || 'Could not load this level.');
    } finally {
      setLoading(false);
    }
  }, [questId]);

  useEffect(() => { loadQuestProgress(); }, [questId, loadQuestProgress]);
  useEffect(() => { if (currentLevel !== null) fetchLevel(currentLevel); }, [currentLevel, questId, fetchLevel]);

  const handleSubmit = async () => {
    if (!userPrompt.trim()) return;
    setSubmitting(true);
    setError('');
    try {
      const response = await API.post(`/quests/${questId}/level/${currentLevel}`, { userPrompt });
      setAiResponse(response.aiResponse);
      setValidation(response.validation);
    } catch (e: any) {
      setError(e?.message || 'Could not submit your prompt. Try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleNextLevel = () => {
    if (level && currentLevel !== null && currentLevel < 10) {
      setCurrentLevel(currentLevel + 1);
    } else {
      navigate('/prompt-quest');
    }
  };

  if (loading || currentLevel === null || !level) {
    if (error) {
      return (
        <div style={{ ...panel, maxWidth: 560, margin: '0 auto' }}>
          <h1 className="ws-display" style={{ margin: '0 0 8px', fontSize: 20 }}>Couldn&rsquo;t load this level</h1>
          <p style={{ margin: '0 0 16px', fontSize: 14, color: 'var(--text-secondary)' }}>{error}</p>
          <button type="button" onClick={() => navigate('/prompt-quest')} className="btn-live" style={{ background: 'var(--ink)', color: 'white', fontWeight: 700, fontSize: 14, padding: '10px 20px', borderRadius: 10, border: 'none', cursor: 'pointer' }}>
            Back to missions
          </button>
        </div>
      );
    }
    return <p style={{ fontSize: 14, color: 'var(--text-tertiary)' }}>Loading level&hellip;</p>;
  }

  return (
    <div style={{ maxWidth: 860, margin: '0 auto', display: 'flex', flexDirection: 'column', gap: 18 }}>
      <button
        type="button"
        onClick={() => navigate('/prompt-quest')}
        style={{ alignSelf: 'flex-start', background: 'none', border: 'none', padding: 0, fontSize: 13.5, fontWeight: 600, color: 'var(--ember-strong)', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}
      >
        <svg width="14" height="14" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><path d="M13 16l-6-6 6-6" /></svg>
        Back to missions
      </button>

      <div className="ws-fade-up" style={panel}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 14, flexWrap: 'wrap', gap: 10 }}>
          <h1 className="ws-display" style={{ margin: 0, fontSize: 20, fontWeight: 700, color: 'var(--text-primary)' }}>Level {currentLevel}/10</h1>
          <span className="ws-mono" style={{ fontSize: 11.5, color: 'var(--text-tertiary)' }}>Difficulty: <strong style={{ color: 'var(--text-secondary)', textTransform: 'capitalize' }}>{level.difficulty}</strong></span>
        </div>
        <div style={{ display: 'flex', gap: 4 }}>
          {[...Array(10)].map((_, i) => (
            <div key={i} style={{ height: 6, flex: 1, borderRadius: 999, background: i < currentLevel ? 'var(--ember)' : 'var(--surface-sunken)', border: i < currentLevel ? 'none' : '1px solid var(--border)' }} />
          ))}
        </div>
      </div>

      <div className="ws-fade-up" style={{ ...panel, animationDelay: '60ms' }}>
        <h2 className="ws-display" style={{ margin: '0 0 16px', fontSize: 16, fontWeight: 700, color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: 8 }}>
          <LucideIcon name="MapPin" size={18} /> {level.title}
        </h2>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div>
            <p className="ws-mono" style={{ margin: '0 0 6px', fontSize: 10.5, fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', color: 'var(--text-tertiary)', display: 'flex', alignItems: 'center', gap: 6 }}>
              <LucideIcon name="BookOpen" size={13} /> Story
            </p>
            <p style={{ margin: 0, fontSize: 14, lineHeight: 1.6, color: 'var(--text-primary)', whiteSpace: 'pre-wrap' }}>{level.story}</p>
          </div>
          <div>
            <p className="ws-mono" style={{ margin: '0 0 6px', fontSize: 10.5, fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', color: 'var(--text-tertiary)', display: 'flex', alignItems: 'center', gap: 6 }}>
              <LucideIcon name="Target" size={13} /> Your objective
            </p>
            <p style={{ margin: 0, fontSize: 14, lineHeight: 1.6, color: 'var(--text-primary)' }}>{level.objective}</p>
          </div>
          {level.hints && level.hints.length > 0 && (
            <div style={{ background: 'var(--ember-soft)', border: '1px solid var(--ember)', borderRadius: 10, padding: '14px 16px' }}>
              <p className="ws-mono" style={{ margin: '0 0 8px', fontSize: 10.5, fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', color: 'var(--ember-strong)', display: 'flex', alignItems: 'center', gap: 6 }}>
                <LucideIcon name="tip" size={13} /> Hints
              </p>
              <ul style={{ margin: 0, padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 4 }}>
                {level.hints.map((hint) => (
                  <li key={hint} style={{ fontSize: 13, color: 'var(--text-secondary)' }}>• {hint}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>

      <div className="ws-fade-up" style={{ ...panel, animationDelay: '100ms' }}>
        <label style={{ display: 'block', fontSize: 13, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 10 }}>Your prompt</label>
        <textarea
          value={userPrompt}
          onChange={(e) => setUserPrompt(e.target.value)}
          placeholder="Write your prompt here…"
          disabled={submitting || (validation?.passed ?? false)}
          className="ws-mono"
          style={{ width: '100%', boxSizing: 'border-box', minHeight: 130, resize: 'vertical', fontSize: 12.5, lineHeight: 1.8, padding: 16, color: 'var(--text-primary)', background: 'var(--surface-sunken)', border: '1px solid var(--border)', borderRadius: 10, outline: 'none' }}
        />
        {error && <p role="alert" className="ws-rise" style={{ margin: '12px 0 0', fontSize: 13.5, color: 'var(--danger, #b42318)' }}>{error}</p>}
        <div style={{ marginTop: 14 }}>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={!userPrompt.trim() || submitting || (validation?.passed ?? false)}
            className="btn-live ws-display"
            style={{
              padding: '11px 24px', borderRadius: 10, border: 'none', fontSize: 14, fontWeight: 700,
              cursor: !userPrompt.trim() || submitting || validation?.passed ? 'not-allowed' : 'pointer',
              background: !userPrompt.trim() || submitting || validation?.passed ? 'var(--surface-sunken)' : 'var(--ember)',
              color: !userPrompt.trim() || submitting || validation?.passed ? 'var(--text-tertiary)' : 'white',
            }}
          >
            {submitting ? 'Submitting…' : 'Submit prompt'}
          </button>
        </div>
      </div>

      {aiResponse && (
        <div className="ws-rise" style={panel}>
          <p className="ws-mono" style={{ margin: '0 0 10px', fontSize: 10.5, fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', color: 'var(--text-tertiary)', display: 'flex', alignItems: 'center', gap: 6 }}>
            <LucideIcon name="Bot" size={13} /> AI response
          </p>
          <pre className="ws-mono" style={{ margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontSize: 12.5, lineHeight: 1.8, color: 'var(--text-primary)', background: 'var(--surface-sunken)', border: '1px solid var(--border)', borderRadius: 10, padding: 16 }}>
            {aiResponse}
          </pre>
        </div>
      )}

      {validation && (
        <div className="ws-rise" style={{
          ...panel,
          background: validation.passed ? 'oklch(0.65 0.15 155 / 0.08)' : 'oklch(0.6 0.2 25 / 0.06)',
          borderColor: validation.passed ? 'var(--success)' : 'var(--danger, #b42318)',
        }}>
          <h3 className="ws-display" style={{ margin: '0 0 10px', fontSize: 15, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 8, color: validation.passed ? 'var(--success)' : 'var(--danger, #b42318)' }}>
            <LucideIcon name={validation.passed ? 'status-pass' : 'status-fail'} size={19} />
            {validation.passed ? 'Level complete!' : 'Try again'}
          </h3>
          <p style={{ margin: validation.breakdown ? '0 0 14px' : (validation.passed ? '0 0 14px' : 0), fontSize: 14, lineHeight: 1.6, color: 'var(--text-primary)' }}>{validation.feedback}</p>

          {validation.breakdown && (
            <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 10, padding: 16, marginBottom: validation.passed ? 14 : 0 }}>
              <p className="ws-mono" style={{ margin: '0 0 12px', fontSize: 10.5, fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', color: 'var(--text-tertiary)', display: 'flex', alignItems: 'center', gap: 6 }}>
                <LucideIcon name="graded-info" size={13} /> How your prompt technique scored
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {RUBRIC_ROWS.map((row) => (
                  <div key={row.key}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 3 }}>
                      <span style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--text-primary)' }}>{row.label}</span>
                      <span className="ws-mono" style={{ fontSize: 12, fontWeight: 700, color: 'var(--ember-strong)' }}>{validation.breakdown![row.key]}/25</span>
                    </div>
                    {validation.breakdown![row.reasoningKey] && (
                      <p style={{ margin: 0, fontSize: 12, lineHeight: 1.5, color: 'var(--text-secondary)', fontStyle: 'italic' }}>{validation.breakdown![row.reasoningKey]}</p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {validation.passed && (
            <button
              type="button"
              onClick={handleNextLevel}
              className="btn-live ws-display"
              style={{ padding: '11px 22px', borderRadius: 10, border: 'none', fontSize: 13.5, fontWeight: 700, cursor: 'pointer', background: 'var(--success)', color: 'white' }}
            >
              {currentLevel < 10 ? 'Next level →' : 'Complete mission'}
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default QuestLevel;
